import { LocalStorageService } from '../../../services/local-storage.service';
import { CacheService } from '../../../services/cache.service';
import { Injectable, OnDestroy } from '@angular/core';
import { forkJoin, Subscription } from 'rxjs';
import L from 'leaflet';
import { DensityHeatLayer } from './density-heat-layer';
import { DENSITY_COLORMAPS, DensityColormap } from './density-colormaps';
import { GridFeatureBase } from '../../core/base/grid-feature.base';
import { DensityCell, DensitySource, DENSITY_RADIUS, DENSITY_BLUR, densityGrids, densityMaximum } from '../../core/density';
import { MapViewport } from '../../core/spatial/map-viewport';
import { CustomerFeature } from '../customers/customer-feature';
import { LeadFeature } from '../leads/lead-feature';
import { AuthService } from '../../../services/auth.service';

export interface DensitySettings {
  source: 'customers' | 'leads';
  opacity: number;
  contrast: number;
  radius: number;
  blur: number;
  colormap: DensityColormap;
}

const DEFAULTS: DensitySettings = {
  source: 'customers', opacity: 70, contrast: 1, radius: DENSITY_RADIUS, blur: DENSITY_BLUR, colormap: 'warm',
};
const STORAGE_KEY = 'map-density-settings@3';


@Injectable({ providedIn: 'root' })
export class DensityFeature extends GridFeatureBase<DensityCell> implements OnDestroy {
  readonly key = 'density';
  readonly name = 'Density';
  override readonly icon = 'blur_on';
  override readonly iconColor = '#e86b24';
  // Analytical overlays are opt-in; the manager persists the eye choice.
  override enabled = false;
  objects: DensityCell[] = [];
  loading = false;
  error: string | null = null;
  hasLoaded = false;
  settings: Readonly<DensitySettings> = this.readSettings();
  private map?: L.Map;
  private heat?: DensityHeatLayer;
  private viewport?: MapViewport;
  private request?: Subscription;
  private readonly sources: Record<DensitySettings['source'], DensitySource>;
  private readonly subscriptions = new Subscription();
  private reference: number | null = null;
  private pendingKey = '';
  private coverage: readonly MapViewport[] = [];

  constructor(customers: CustomerFeature, leads: LeadFeature, private readonly auth: AuthService, private readonly cache: CacheService, private readonly storage: LocalStorageService) {
    super();
    this.sources = { customers, leads };
    for (const source of ['customers', 'leads'] as const) {
      this.subscriptions.add(this.sources[source].densityChanged$.subscribe(() => {
        if (this.settings.source === source) this.reload();
      }));
    }
    if (!this.canUse(this.settings.source) && this.canUse('leads')) {
      this.settings = { ...this.settings, source: 'leads' };
    }
  }

  async init(): Promise<void> {
    if (!this.canUse(this.settings.source)) {
      if (this.canUse('customers')) this.settings = { ...this.settings, source: 'customers' };
      else if (this.canUse('leads')) this.settings = { ...this.settings, source: 'leads' };
    }
  }

  resetSession(): void {
    this.clear();
    this.settings = this.readSettings();
    this.viewport = undefined;
    this.reference = null;
    this.error = null;
    this.notifyChanged();
  }

  get contextState(): Readonly<Record<string, unknown>> { return { settings: { ...this.settings } }; }

  get sourceLabel(): string { return this.settings.source === 'customers' ? 'Customers' : 'Leads'; }
  get gradient(): string { return DENSITY_COLORMAPS[this.settings.colormap].legend; }

  canUse(source: DensitySettings['source']): boolean {
    return this.auth.hasPermission(source === 'customers' ? 'Customer.View' : 'Lead.View');
  }

  attach(map: L.Map): void {
    this.detach();
    this.map = map;
    this.heat = new DensityHeatLayer();
    if (this.enabled) this.render();
  }

  detach(): void {
    this.clear();
    this.heat = undefined;
    this.map = undefined;
    this.viewport = undefined;
    this.reference = null;
  }

  onViewportChanged(viewport: MapViewport): void {
    if (viewport.zoom !== (this.viewport?.zoom ?? viewport.zoom)) {
      this.reference = null;
    }
    this.viewport = viewport;
    if (this.enabled) this.refresh();
  }

  update(patch: Partial<DensitySettings>): void {
    const next = { ...this.settings, ...patch };
    if (!this.canUse(next.source)) return;
    next.opacity = Math.max(10, Math.min(100, Number(next.opacity) || DEFAULTS.opacity));
    next.contrast = Math.max(0.25, Math.min(4, Number(next.contrast) || DEFAULTS.contrast));
    next.radius = Number.isFinite(next.radius) ? Math.round(Math.max(8, Math.min(96, next.radius))) : DEFAULTS.radius;
    next.blur = Number.isFinite(next.blur) ? Math.round(Math.max(0, Math.min(64, next.blur))) : DEFAULTS.blur;
    if (!Object.hasOwn(DENSITY_COLORMAPS, next.colormap)) next.colormap = DEFAULTS.colormap;
    const kernelChanged = next.radius !== this.settings.radius || next.blur !== this.settings.blur;
    const reload = next.source !== this.settings.source;
    this.settings = next;
    if (reload || kernelChanged) this.reference = null;
    this.storage.set(STORAGE_KEY, next);
    if (reload) this.reload();
    else {
      if (kernelChanged) this.refresh();
      this.draw();
      this.notifyChanged();
    }
  }

  reset(): void { this.update({ ...DEFAULTS, source: this.settings.source }); }

  reload(): void {
    this.cache.clear('density');
    this.request?.unsubscribe();
    this.coverage = [];
    this.pendingKey = '';
    this.reference = null;
    this.objects = [];
    this.hasLoaded = false;
    this.heat?.clear();
    if (this.enabled) this.refresh();
  }

  override render(): void {
    if (!this.enabled) return;
    if (this.heat && !this.layer.hasLayer(this.heat)) this.layer.addLayer(this.heat);
    this.refresh();
  }

  override clear(): void {
    this.cache.clear('density');
    this.request?.unsubscribe();
    this.loading = false;
    this.coverage = [];
    this.pendingKey = '';
    this.hasLoaded = false;
    this.objects = [];
    this.heat?.clear();
    super.clear();
  }

  ngOnDestroy(): void { this.detach(); this.subscriptions.unsubscribe(); }

  private refresh(): void {
    if (!this.map || !this.viewport || !this.enabled) return;
    if (!this.canUse(this.settings.source)) {
      this.clear();
      this.error = `You do not have permission to view ${this.sourceLabel.toLowerCase()}.`;
      this.notifyChanged();
      return;
    }
    const required = densityGrids(this.viewport, this.settings.radius + this.settings.blur);
    if (required.every(part => this.coverage.some(loaded => loaded.zoom === part.zoom &&
        loaded.west <= part.west && loaded.east >= part.east && loaded.south <= part.south && loaded.north >= part.north))) {
      this.request?.unsubscribe();
      this.pendingKey = '';
      this.loading = false;
      this.hasLoaded = true;
      this.error = null;
      this.draw();
      this.notifyChanged();
      return;
    }
    // One padded data window absorbs small pans without another request.
    const grids = densityGrids(this.viewport, 192);
    const key = JSON.stringify(grids);
    if (key === this.pendingKey) return;
    this.request?.unsubscribe();
    this.pendingKey = key;
    this.loading = true;
    this.error = null;
    this.hasLoaded = false;
    this.notifyChanged();
    this.request = this.cache.load('density', key, () =>
      forkJoin(grids.map(grid => this.sources[this.settings.source].loadDensity(grid))),
      { capacity: 1 })
      .subscribe({
        next: responses => {
          const cells = new Map<string, DensityCell>();
          for (const cell of responses.flat()) {
            const id = `${cell.zoom}:${cell.tileX}:${cell.tileY}`;
            cells.set(id, { ...cell, id });
          }
          this.objects = [...cells.values()];
          this.loading = false;
          this.pendingKey = '';
          this.coverage = grids;
          this.hasLoaded = true;
          this.draw();
          this.notifyChanged();
        },
        error: () => {
          this.pendingKey = '';
          this.loading = false;
          this.error = 'Density could not be loaded. Try again.';
          this.notifyChanged();
        },
      });
  }

  private draw(): void {
    if (!this.map || !this.viewport || !this.heat || !this.enabled) return;
    if (!this.layer.hasLayer(this.heat)) this.layer.addLayer(this.heat);
    const maximum = this.reference ?? densityMaximum(this.objects, this.viewport, this.settings.radius, this.settings.blur);
    // A pending border refill may add neighbors; only retain a complete view's reference.
    if (this.hasLoaded && this.objects.length) this.reference = maximum;
    this.heat.setData(this.objects, {
      opacity: this.settings.opacity,
      radius: this.settings.radius,
      blur: this.settings.blur,
      maximum: maximum / this.settings.contrast,
      gradient: DENSITY_COLORMAPS[this.settings.colormap].gradient,
    });
  }

  private readSettings(): DensitySettings {
    try {
      const value = this.storage.get<Partial<DensitySettings>>(STORAGE_KEY) ?? this.storage.get<Partial<DensitySettings>>('map-density-settings@2');
      return {
        source: value?.source === 'leads' ? 'leads' : 'customers',
        opacity: typeof value?.opacity === 'number' && value.opacity >= 10 && value.opacity <= 100 ? value.opacity : DEFAULTS.opacity,
        contrast: typeof value?.contrast === 'number' && value.contrast >= 0.25 && value.contrast <= 4 ? value.contrast : DEFAULTS.contrast,
        radius: typeof value?.radius === 'number' && value.radius >= 8 && value.radius <= 96 ? Math.round(value.radius) : DEFAULTS.radius,
        blur: typeof value?.blur === 'number' && value.blur >= 0 && value.blur <= 64 ? Math.round(value.blur) : DEFAULTS.blur,
        colormap: value?.colormap && Object.hasOwn(DENSITY_COLORMAPS, value.colormap) ? value.colormap : DEFAULTS.colormap,
      };
    } catch { return { ...DEFAULTS }; }
  }
}
